import React, { useMemo } from 'react';
import { getDataByPath } from '../../utils/dataHelpers';
import { getAvailableFields, getNestedValueForFilters } from '../../utils/widgetHelpers';
import SearchableSelect from '../SearchableSelect';

const DeviceFiltersConfiguration = ({ block, scanData, onUpdateFilters, dataContext, filters }) => {
    // If filters prop is provided (e.g. for segments), use it. Otherwise use block config.
    const deviceFilters = filters || block.dataConfig?.deviceFilters || [];
    const metricPath = block.dataConfig?.metricPath;
    const browserSelection = block.dataConfig?.browserSelection;

    // Get the effective path for data lookup
    const effectivePath = browserSelection?.path || metricPath;

    // Get available fields from data
    const availableFields = useMemo(() => {
        const dataSource = dataContext || scanData;
        
        // Get raw fields first using the improved helper
        const fields = getAvailableFields(dataSource, effectivePath);
        const firstItem = getDataByPath(dataSource, effectivePath)?.[0];
        
        // Enhance with metadata for filter UI
        return fields.map(fieldPath => {
            const fieldData = getNestedValueForFilters(firstItem, fieldPath);
            // Try to determine type from first item
            let type = 'string';
            let sampleValue = fieldData;
            
            if (fieldData === null || fieldData === undefined) {
                type = 'unknown';
            } else if (Array.isArray(fieldData)) {
                type = 'array';
                sampleValue = `[Array of ${fieldData.length}]`;
            } else if (typeof fieldData === 'object') {
                type = 'object';
                sampleValue = '{Object}';
            } else if (typeof fieldData === 'boolean') {
                type = 'boolean';
            } else if (typeof fieldData === 'number') {
                type = 'number';
            }

            // Check if it's a known nested path
             const parts = fieldPath.split('.');
             const isNested = parts.length > 1;

            return {
                label: fieldPath,
                path: fieldPath,
                value: fieldPath, // Adapting for SearchableSelect
                type,
                sampleValue,
                isArrayPath: isNested
            };
        });
    }, [metricPath, scanData, dataContext, effectivePath]);

    // When flatten is active, add _flattenedKey/_flattenedValue and discover _flattenedItem.* sub-fields
    const flattenField = block.dataConfig?.flattenField;
    const effectiveFields = useMemo(() => {
        if (!flattenField) return availableFields;
        const extraFields = [
            { label: '_flattenedKey (port number, key name, etc.)', path: '_flattenedKey', value: '_flattenedKey', type: 'string', sampleValue: '', isArrayPath: false },
            { label: '_flattenedValue', path: '_flattenedValue', value: '_flattenedValue', type: 'string', sampleValue: '', isArrayPath: false }
        ];
        
        // Discover sub-fields from the flatten target array items
        const dataSource = dataContext || scanData;
        const rawData = dataSource ? getDataByPath(dataSource, effectivePath || metricPath) : null;
        if (rawData && Array.isArray(rawData)) {
            const subFieldsSet = new Set();
            const samplesToScan = Math.min(rawData.length, 50);
            for (let i = 0; i < samplesToScan; i++) {
                const item = rawData[i];
                if (!item || typeof item !== 'object') continue;
                const parts = flattenField.split('.');
                let target = item;
                for (const part of parts) {
                    if (!target || typeof target !== 'object') { target = null; break; }
                    target = target[part];
                }
                if (!target || typeof target !== 'object') continue;
                if (Array.isArray(target)) {
                    for (let ai = 0; ai < Math.min(target.length, 5); ai++) {
                        const arrItem = target[ai];
                        if (arrItem && typeof arrItem === 'object' && !Array.isArray(arrItem)) {
                            const collect = (obj, prefix, depth = 0) => {
                                if (depth > 3 || !obj || typeof obj !== 'object') return;
                                Object.keys(obj).forEach(key => {
                                    if (key.startsWith('_')) return;
                                    const subPath = prefix ? `${prefix}.${key}` : key;
                                    subFieldsSet.add(subPath);
                                    const val = obj[key];
                                    if (val && typeof val === 'object' && !Array.isArray(val)) {
                                        collect(val, subPath, depth + 1);
                                    }
                                });
                            };
                            collect(arrItem, '');
                        }
                    }
                }
            }
            // Add discovered sub-fields as _flattenedItem.* options
            Array.from(subFieldsSet).sort().forEach(sf => {
                extraFields.push({
                    label: `_flattenedItem.${sf}`,
                    path: `_flattenedItem.${sf}`,
                    value: `_flattenedItem.${sf}`,
                    type: 'string', sampleValue: '', isArrayPath: false
                });
            });
        }
        
        return [...extraFields, ...availableFields];
    }, [availableFields, flattenField, dataContext, scanData, effectivePath, metricPath]);

    // Filter operators
    const operators = [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Not Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'greater_than', label: 'Greater Than (>)' },
        { value: 'greater_than_or_equal', label: 'Greater Than or Equal (>=)' },
        { value: 'less_than', label: 'Less Than (<)' },
        { value: 'less_than_or_equal', label: 'Less Than or Equal (<=)' },
        { value: 'is_empty', label: 'Is Empty/Null' },
        { value: 'is_not_empty', label: 'Is Not Empty' },
        // { value: 'in_list', label: 'In List (Predefined)' },
        // { value: 'not_in_list', label: 'Not In List (Predefined)' },
        { value: 'is_true', label: 'Is True' },
        { value: 'is_false', label: 'Is False' }
    ];

    // Predefined value lists for 'in_list' / 'not_in_list' operators
    const predefinedLists = {
        sensitive_ports: {
            label: 'Sensitive Ports (21, 22, 23, 25, 53, 80, 139, 143, 3306, 3389, 8080)',
            values: ['21', '22', '23', '25', '53', '80', '139', '143', '3306', '3389', '8080']
        }
    };

    // Array quantifier options for array-traversing paths
    const arrayQuantifiers = [
        { value: 'any_match', label: 'Any item matches' },
        { value: 'all_match', label: 'All items match' },
        { value: 'none_match', label: 'No items match' }
    ];

    // Check if a field path traverses through an array
    const isFieldArrayPath = (fieldPath) => {
        const field = availableFields.find(f => f.path === fieldPath);
        return field?.isArrayPath || false;
    };

    // Check if operator requires a value input
    const operatorNeedsValue = (operator) => {
        return !['is_empty', 'is_not_empty', 'is_true', 'is_false', 'in_list', 'not_in_list'].includes(operator);
    };

    // Check if operator uses predefined lists
    const operatorUsesList = (operator) => {
        return ['in_list', 'not_in_list'].includes(operator);
    };

    const addFilter = () => {
        const newFilter = {
            id: `filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            fieldPath: '',
            operator: 'equals',
            value: '',
            arrayQuantifier: 'any_match', // Default: at least one item matches
            logic: deviceFilters.length === 0 ? 'AND' : 'AND'
        };
        console.log('[DeviceFilters] Adding filter:', newFilter);
        console.log('[DeviceFilters] Current filters:', deviceFilters);
        onUpdateFilters([...deviceFilters, newFilter]);
    };

    const removeFilter = (filterId) => {
        console.log('[DeviceFilters] Removing filter:', filterId);
        onUpdateFilters(deviceFilters.filter(f => f.id !== filterId));
    };

    const updateFilter = (filterId, updates) => {
        console.log('[DeviceFilters] Updating filter:', filterId, updates);
        onUpdateFilters(deviceFilters.map(f =>
            f.id === filterId ? { ...f, ...updates } : f
        ));
    };

    // Get sample values for selected field (for autocomplete/hints)
    const getSampleValuesForField = (fieldPath) => {
        if (!effectivePath || !fieldPath) return [];

        const data = getDataByPath(dataContext || scanData, effectivePath);
        if (!Array.isArray(data)) return [];

        const values = new Set();
        data.slice(0, 50).forEach(item => {
            const value = getNestedValueForFilters(item, fieldPath);
            if (value !== undefined && value !== null) {
                // Handle different value types appropriately
                if (typeof value === 'object') {
                    if (Array.isArray(value)) {
                        // For arrays, show meaningful representation
                        if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                            // Array of objects - try to find meaningful fields
                            const firstItem = value[0];
                            const nameField = Object.keys(firstItem).find(k =>
                                ['name', 'title', 'label', 'model', 'Model', 'Name', 'Title'].includes(k)
                            );
                            if (nameField && firstItem[nameField]) {
                                values.add(`Has ${String(firstItem[nameField])}`);
                            } else {
                                values.add(`Has ${value.length} item(s)`);
                            }
                        } else if (value.length > 0) {
                            // Array of primitives
                            values.add(value.slice(0, 3).join(', '));
                        }
                    } else {
                        // Regular object - show as JSON
                        values.add(JSON.stringify(value));
                    }
                } else {
                    // Primitive value
                    values.add(String(value));
                }
            }
        });
        return Array.from(values).slice(0, 20);
    };

    return (
        <div className="device-filters-config" style={{ marginTop: '1rem' }}>
            <div className="property-field">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    🔍 {filters ? 'Segment Filters' : 'Device/Parent Filters'}
                </label>
                <small className="help-text" style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                    {filters 
                        ? 'Define filters that items must match to be counted in this segment.' 
                        : 'Filter the main data source (e.g., network_devices) by properties like "RDP Enabled", "wifi", etc. These filters are applied BEFORE any other processing.'}
                </small>

                {deviceFilters.length === 0 && (
                    <div style={{
                        padding: '0.75rem',
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        border: '1px dashed var(--border)',
                        borderRadius: 'var(--radius)',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem'
                    }}>
                        {filters ? 'No filters configured for this segment.' : 'No device filters configured. Add filters to narrow down which devices/items to include.'}
                    </div>
                )}

                {deviceFilters.map((filter, index) => (
                    <div key={filter.id} style={{
                        marginBottom: '0.75rem',
                        padding: '0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        backgroundColor: 'var(--background)'
                    }}>
                        {/* Logic selector for subsequent filters */}
                        {index > 0 && (
                            <div style={{ marginBottom: '0.5rem' }}>
                                <select
                                    value={filter.logic || 'AND'}
                                    onChange={(e) => updateFilter(filter.id, { logic: e.target.value })}
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--border)',
                                        backgroundColor: filter.logic === 'OR' ? 'var(--warning)' : 'var(--primary)',
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    <option value="AND">AND</option>
                                    <option value="OR">OR</option>
                                </select>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <strong style={{ fontSize: '0.875rem' }}>Filter {index + 1}</strong>
                            <button
                                type="button"
                                onClick={() => removeFilter(filter.id)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--error)',
                                    cursor: 'pointer',
                                    fontSize: '1.25rem',
                                    padding: '0',
                                    lineHeight: '1'
                                }}
                                title="Remove filter"
                            >
                                ×
                            </button>
                        </div>

                        {/* Field Path - Searchable Dropdown */}
                        <div style={{ marginBottom: '0.5rem' }}>
                            <SearchableSelect 
                                value={filter.fieldPath || ''}
                                onChange={(newPath) => updateFilter(filter.id, { fieldPath: newPath })}
                                options={effectiveFields}
                                placeholder="Select field to filter by..."
                            />
                        </div>

                        {/* Array Quantifier (shown for array-traversing paths) */}
                        {/* {isFieldArrayPath(filter.fieldPath) && (
                            <div style={{ marginBottom: '0.5rem' }}>
                                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                                    📋 This field is inside a nested array. Match when:
                                </label>
                                <select
                                    value={filter.arrayQuantifier || 'any_match'}
                                    onChange={(e) => updateFilter(filter.id, { arrayQuantifier: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--primary)',
                                        backgroundColor: 'var(--primary-light)',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {arrayQuantifiers.map(q => (
                                        <option key={q.value} value={q.value}>{q.label}</option>
                                    ))}
                                </select>
                            </div>
                        )} */}

                        {/* Operator */}
                        <div style={{ marginBottom: '0.5rem' }}>
                            <select
                                value={filter.operator || 'equals'}
                                onChange={(e) => updateFilter(filter.id, { operator: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: 'var(--radius)',
                                    border: '1px solid var(--border)',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {operators.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Value (only shown for operators that need it) */}
                        {operatorNeedsValue(filter.operator) && (
                            <div>
                                <input
                                    type="text"
                                    value={filter.value || ''}
                                    onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                                    placeholder="Enter value..."
                                    list={`filter-values-${filter.id}`}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--border)',
                                        fontSize: '0.875rem'
                                    }}
                                />
                                {filter.fieldPath && (
                                    <datalist id={`filter-values-${filter.id}`}>
                                        {getSampleValuesForField(filter.fieldPath).map((val, idx) => (
                                            <option key={idx} value={val} />
                                        ))}
                                    </datalist>
                                )}
                            </div>
                        )}

                        {/* Predefined List selector (for in_list / not_in_list operators) */}
                        {operatorUsesList(filter.operator) && (
                            <div style={{ marginBottom: '0.5rem' }}>
                                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.25rem' }}>
                                    📋 Select a predefined list or enter comma-separated values:
                                </label>
                                <select
                                    value={filter.value || ''}
                                    onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--border)',
                                        fontSize: '0.875rem',
                                        marginBottom: '0.25rem'
                                    }}
                                >
                                    <option value="">-- Select a list --</option>
                                    {Object.entries(predefinedLists).map(([key, list]) => (
                                        <option key={key} value={key}>{list.label}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={Object.keys(predefinedLists).includes(filter.value) ? '' : (filter.value || '')}
                                    onChange={(e) => updateFilter(filter.id, { value: e.target.value })}
                                    placeholder="Or enter comma-separated values (e.g. 22,80,443)"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: 'var(--radius)',
                                        border: '1px solid var(--border)',
                                        fontSize: '0.8rem',
                                        color: 'var(--text-secondary)'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ))}

                <button
                    type="button"
                    onClick={addFilter}
                    style={{
                        width: '100%',
                        padding: '0.5rem',
                        background: 'var(--primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        marginTop: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem'
                    }}
                >
                    + Add Device Filter
                </button>

                {deviceFilters.length > 0 && (
                    <button
                        type="button"
                        onClick={() => onUpdateFilters([])}
                        style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: 'transparent',
                            color: 'var(--error)',
                            border: '1px solid var(--error)',
                            borderRadius: 'var(--radius)',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            marginTop: '0.5rem'
                        }}
                    >
                        Clear All Filters
                    </button>
                )}
            </div>
        </div>
    );
};

export default DeviceFiltersConfiguration;
